import React from "react";
import { Phone, MapPin, Clock, Instagram, Facebook, Heart } from "lucide-react";
import { Link } from "react-router-dom";

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-sage-dark text-white py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <h3 className="text-3xl font-playfair font-bold text-gold-warm mb-4">
              Fonte de Juvence
            </h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              O seu santuário de bem-estar no coração de Braga. Descubra tratamentos 
              exclusivos que realçam a sua beleza natural e proporcionam momentos únicos 
              de relaxamento e rejuvenescimento.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.instagram.com/fontedejuvence/" 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 w-10 h-10 rounded-full flex items-center justify-center hover:bg-gold-warm transition-colors duration-300"
                aria-label="Instagram da Fonte de Juvence"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/p/FONTE-DE-JUVENCE-100063727261156/?locale=pt_PT" 
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 w-10 h-10 rounded-full flex items-center justify-center hover:bg-gold-warm transition-colors duration-300"
                aria-label="Facebook da Fonte de Juvence"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xl font-semibold mb-6">Links Rápidos</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('inicio')}
                  className="text-gray-300 hover:text-gold-warm transition-colors duration-200"
                >
                  Início
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('sobre')}
                  className="text-gray-300 hover:text-gold-warm transition-colors duration-200"
                >
                  Sobre Nós
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('servicos')}
                  className="text-gray-300 hover:text-gold-warm transition-colors duration-200"
                >
                  Serviços
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('precos')}
                  className="text-gray-300 hover:text-gold-warm transition-colors duration-200"
                >
                  Preços
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contacto')}
                  className="text-gray-300 hover:text-gold-warm transition-colors duration-200"
                >
                  Contacto
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xl font-semibold mb-6">Contacto</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gold-warm mt-1 flex-shrink-0" />
                <div>
                  <a href="tel:253279717" className="text-gray-300 hover:text-gold-warm transition-colors">
                    253 279 717
                  </a>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gold-warm mt-1 flex-shrink-0" />
                <div className="text-gray-300">
                  R. de Santo André 51<br />
                  4710-309 Braga
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-gold-warm mt-1 flex-shrink-0" />
                <div className="text-gray-300 text-sm">
                  <p>Ter-Sex: 09:30-19:00</p>
                  <p>Sáb: 09:30-19:00</p>
                  <p>Dom-Seg: Encerrado</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/20 pt-8">
          {/* Legal info */}
          <div className="mb-6 text-center text-gray-400 text-sm">
            <p className="mb-2"><strong className="text-white">Fonte de Juvence, Lda</strong></p>
            <p className="mb-1">NIPC: 508 627 257</p>
            <p className="mb-4">R. de Santo André 51, 4710-309 Braga</p>
            <div className="flex justify-center space-x-4 text-xs">
              <a 
                href="https://www.livroreclamacoes.pt/Inicio/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-gold-warm transition-colors"
              >
                📋 Livro de Reclamações
              </a>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
            <div className="flex items-center space-x-1 mb-4 md:mb-0">
              <span>© 2024 Fonte de Juvence. Criado com</span>
              <Heart className="w-4 h-4 text-red-400 fill-current" />
              <span>em Braga</span>
            </div>
            <div className="flex space-x-6">
              <Link 
                to="/politica-privacidade" 
                className="hover:text-gold-warm transition-colors"
              >
                Política de Privacidade
              </Link>
              <Link 
                to="/termos-servico" 
                className="hover:text-gold-warm transition-colors"
              >
                Termos de Serviço
              </Link>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-8 text-center">
          <div className="bg-gradient-to-r from-gold-warm to-gold-deep rounded-2xl p-6">
            <h4 className="text-xl font-semibold mb-2">Pronto para a sua transformação?</h4>
            <p className="text-gold-light mb-4">Entre em contacto connosco para mais informações</p>
            <a 
              href="tel:253279717"
              className="inline-block bg-white text-gold-warm px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
            >
              Ligar Agora
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;